import { X, Trophy, Goal, Crown, Medal, Lock, Check, Loader2 } from 'lucide-react';
import { initials } from '@isoft-people/utils';
import { useWcBreakdown, type WcBreakdownMatch } from '@/lib/queries';
import { Flag, PHASE_SHORT } from './common';
import { cn } from '@/lib/cn';

function HeaderAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  if (photoUrl) return <img src={photoUrl} alt={name} className="h-12 w-12 rounded-full object-cover" />;
  return (
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-light text-base font-bold text-white">
      {initials(name)}
    </span>
  );
}

/** Una fila por partido finalizado: pronóstico vs resultado y por qué sumó esos puntos. */
function MatchRow({ m }: { m: WcBreakdownMatch }) {
  const gotPoints = m.points > 0;
  return (
    <div className="rounded-lg border border-border-light p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold text-font-tertiary">
          <span className="rounded bg-primary-subtle px-1.5 py-0.5 text-primary">{PHASE_SHORT[m.phase]}</span>
          Partido #{m.number}
        </span>
        <span
          className={cn(
            'flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold',
            gotPoints ? 'bg-accent-light text-accent' : 'bg-bg text-font-tertiary',
          )}
        >
          <Trophy size={11} /> {m.points} pts
        </span>
      </div>

      {/* Equipos + resultado real vs pronóstico */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
        <span className="flex min-w-0 items-center justify-end gap-1.5 text-right font-medium">
          <span className="truncate">{m.homeTeam.nameEs}</span>
          <Flag iso2={m.homeTeam.iso2} className="h-4 w-6 flex-shrink-0" />
        </span>
        <span className="flex flex-col items-center">
          <span className="font-extrabold tabular-nums text-font-primary">
            {m.result.home}-{m.result.away}
          </span>
          {m.scoredAgainst ? (
            <span className="text-center text-[9px] leading-tight text-font-tertiary">
              se calificó con el {m.scoredAgainst.home}-{m.scoredAgainst.away} del min. 90
            </span>
          ) : (
            <span className="text-[9px] uppercase tracking-wide text-font-tertiary">real</span>
          )}
        </span>
        <span className="flex min-w-0 items-center gap-1.5 font-medium">
          <Flag iso2={m.awayTeam.iso2} className="h-4 w-6 flex-shrink-0" />
          <span className="truncate">{m.awayTeam.nameEs}</span>
        </span>
      </div>

      {/* Su pronóstico */}
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-light pt-2 text-xs">
        <span className="text-font-secondary">
          Pronóstico:{' '}
          <span className="font-semibold text-font-primary tabular-nums">
            {m.prediction.home}-{m.prediction.away}
          </span>
        </span>
        {m.prediction.scorerJersey && (
          <span className="flex items-center gap-1 text-font-secondary">
            <Goal size={12} className="text-primary" /> {m.prediction.scorerJersey}
            {m.prediction.scorerHit ? (
              <span className="flex items-center gap-0.5 font-semibold text-accent">
                <Check size={11} /> anotó
              </span>
            ) : (
              <span className="text-font-tertiary">no anotó</span>
            )}
          </span>
        )}
      </div>

      {/* Razones del puntaje */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {m.reasons.map((r, i) => (
          <span
            key={i}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
              r.pts > 0 ? 'bg-accent-light text-accent' : 'bg-bg text-font-tertiary',
            )}
          >
            {r.label} {r.pts > 0 ? `+${r.pts}` : '+0'}
          </span>
        ))}
        {m.multiplier !== 1 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-bold text-warning">
            × {m.multiplier} ({PHASE_SHORT[m.phase]})
          </span>
        )}
      </div>
    </div>
  );
}

export function BreakdownModal({
  userId,
  rank,
  onClose,
}: {
  userId: string;
  rank?: number;
  onClose: () => void;
}) {
  const { data, isLoading } = useWcBreakdown(userId);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border bg-bg-sidebar px-5 py-4 text-white">
          {data ? (
            <div className="flex min-w-0 items-center gap-3">
              <HeaderAvatar name={data.name} photoUrl={data.photoUrl} />
              <div className="min-w-0">
                <p className="truncate text-base font-bold">{data.name}</p>
                <p className="text-xs text-white/70">
                  {rank ? `#${rank} · ` : ''}
                  {data.totalPoints} pts {data.isSelf && '· tú'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Trophy size={18} className="text-primary-light" />
              <h3 className="text-base font-bold">Detalle de puntos</h3>
            </div>
          )}
          <button onClick={onClose} className="flex-shrink-0 rounded-full p-1 text-white/70 hover:bg-white/10 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Resumen */}
        {data && (
          <div className="grid grid-cols-3 divide-x divide-border border-b border-border bg-bg text-center">
            <div className="px-2 py-3">
              <p className="text-lg font-extrabold text-primary tabular-nums">{data.totalPoints}</p>
              <p className="text-[10px] uppercase tracking-wide text-font-tertiary">Total</p>
            </div>
            <div className="px-2 py-3">
              <p className="text-lg font-extrabold text-font-primary tabular-nums">{data.matchPoints}</p>
              <p className="text-[10px] uppercase tracking-wide text-font-tertiary">De partidos</p>
            </div>
            <div className="px-2 py-3">
              <p className="text-lg font-extrabold text-font-primary tabular-nums">{data.bonusPoints}</p>
              <p className="text-[10px] uppercase tracking-wide text-font-tertiary">De torneo</p>
            </div>
          </div>
        )}

        {/* Cuerpo */}
        <div className="flex-1 space-y-2.5 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-font-tertiary">
              <Loader2 size={16} className="animate-spin" /> Cargando detalle…
            </div>
          )}

          {data && data.matches.length === 0 && (
            <div className="py-10 text-center text-sm text-font-secondary">
              Aún no tiene puntos de partidos finalizados.
            </div>
          )}

          {data?.matches.map((m) => <MatchRow key={m.number} m={m} />)}

          {/* Pronósticos de torneo */}
          {data && (
            <div className="mt-2 rounded-lg border border-border-light bg-bg/50 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-font-tertiary">
                <Crown size={13} className="text-warning" /> Pronósticos de torneo
                <span className="ml-auto rounded-full bg-primary-subtle px-2 py-0.5 text-[11px] font-bold text-primary">
                  {data.bonusPoints} pts
                </span>
              </p>
              {data.bonus.revealed ? (
                <div className="space-y-1.5 text-xs">
                  <BonusLine icon={Crown} color="text-warning" label="Campeón" team={data.bonus.champion} hit={data.bonus.breakdown?.champion} />
                  <BonusLine icon={Medal} color="text-info" label="Subcampeón" team={data.bonus.runnerUp} hit={data.bonus.breakdown?.finalist} />
                  <div className="flex items-center gap-2">
                    <Goal size={13} className="flex-shrink-0 text-accent" />
                    <span className="text-font-secondary">Bota de Oro:</span>
                    {data.bonus.goldenBoot ? (
                      <span className="flex items-center gap-1.5 font-semibold text-font-primary">
                        <Flag iso2={data.bonus.goldenBoot.team.iso2} className="h-3 w-4" />
                        {data.bonus.goldenBoot.jersey}
                      </span>
                    ) : (
                      <span className="text-font-tertiary">sin elegir</span>
                    )}
                  </div>
                  <p className="pt-1 text-[10px] text-font-tertiary">
                    Campeón, subcampeón y Bota de Oro se otorgan al terminar el Mundial. Durante el torneo no suman
                    puntos: la tabla refleja solo lo ganado en los partidos.
                  </p>
                </div>
              ) : (
                <p className="flex items-center gap-1.5 text-xs text-font-tertiary">
                  <Lock size={12} /> Sus elecciones de campeón, subcampeón y Bota de Oro se revelan al cerrar el plazo (inicio de octavos).
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BonusLine({
  icon: Icon,
  color,
  label,
  team,
  hit,
}: {
  icon: any;
  color: string;
  label: string;
  team: { nameEs: string; iso2: string | null } | null;
  hit?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={13} className={cn('flex-shrink-0', color)} />
      <span className="text-font-secondary">{label}:</span>
      {team ? (
        <span className="flex items-center gap-1.5 font-semibold text-font-primary">
          <Flag iso2={team.iso2} className="h-3 w-4" /> {team.nameEs}
        </span>
      ) : (
        <span className="text-font-tertiary">sin elegir</span>
      )}
      {hit && (
        <span className="ml-auto flex items-center gap-0.5 text-[11px] font-semibold text-accent">
          <Check size={11} /> acertó
        </span>
      )}
    </div>
  );
}
